import 'mapbox-gl/dist/mapbox-gl.css'

import { useMemo, useState } from 'react'
import { Map as MapboxMap, Marker, Popup } from 'react-map-gl/mapbox'

import { MAPBOX_KEY } from '@/config'

export interface MapPoint {
  lat: number
  lng: number
  label: string
  count?: number
  total?: number | null
  // Wire field is `transaction_ids`; camelcaseSpec converts.
  transactionIds?: string[] | null
}

export interface MapSpec {
  title?: string | null
  points: MapPoint[]
}

interface MapArtifactProps {
  spec: MapSpec
}

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
})

const DEFAULT_HEIGHT = 360
const MIN_ZOOM = 1
const MAX_ZOOM = 14

export default function MapArtifact({ spec }: MapArtifactProps) {
  const { title, points } = spec
  const headline = title?.trim() || 'Transaction locations'
  const [activePoint, setActivePoint] = useState<MapPoint | null>(null)

  const initialView = useMemo(() => computeInitialView(points), [points])

  if (!MAPBOX_KEY) {
    return (
      <figure className="my-1 w-full rounded-lg border bg-card p-4">
        <div className="text-sm font-medium">{headline}</div>
        <div className="mt-2 text-xs text-muted-foreground">
          Map cannot render — VITE_MAPBOX_KEY is not configured.
        </div>
      </figure>
    )
  }

  if (points.length === 0) {
    return (
      <figure className="my-1 w-full rounded-lg border bg-card p-4">
        <div className="text-sm font-medium">{headline}</div>
        <div className="mt-2 text-xs text-muted-foreground">
          No locations to plot.
        </div>
      </figure>
    )
  }

  return (
    <figure className="my-1 w-full overflow-hidden rounded-lg border bg-card">
      <div className="border-b px-4 py-2 text-sm font-medium text-foreground">
        {headline}
      </div>
      <div style={{ height: DEFAULT_HEIGHT }}>
        <MapboxMap
          mapboxAccessToken={MAPBOX_KEY}
          initialViewState={initialView}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
        >
          {points.map((point) => (
            <Marker
              key={`${point.lat},${point.lng},${point.label}`}
              latitude={point.lat}
              longitude={point.lng}
              anchor="bottom"
              onClick={(event) => {
                event.originalEvent.stopPropagation()
                setActivePoint(point)
              }}
            >
              <MarkerPin count={point.count ?? 1} />
            </Marker>
          ))}
          {activePoint && (
            <Popup
              latitude={activePoint.lat}
              longitude={activePoint.lng}
              anchor="top"
              onClose={() => setActivePoint(null)}
              closeOnClick={false}
            >
              <div className="text-xs">
                <div className="font-medium text-foreground">
                  {activePoint.label}
                </div>
                <div className="mt-0.5 text-muted-foreground">
                  {pluralize(activePoint.count ?? 1, 'transaction')}
                  {typeof activePoint.total === 'number'
                    ? ` · ${currencyFormatter.format(activePoint.total)}`
                    : null}
                </div>
              </div>
            </Popup>
          )}
        </MapboxMap>
      </div>
    </figure>
  )
}

function MarkerPin({ count }: { count: number }) {
  const showBadge = count > 1
  return (
    <div className="relative flex flex-col items-center">
      <div className="size-3 rounded-full border-2 border-white bg-primary shadow" />
      {showBadge && (
        <div className="absolute -top-3 left-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground shadow">
          {count}
        </div>
      )}
    </div>
  )
}

function pluralize(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? '' : 's'}`
}

function computeInitialView(points: MapPoint[]) {
  if (points.length === 0) {
    return { latitude: 39.5, longitude: -98.35, zoom: 3 }
  }
  if (points.length === 1) {
    return { latitude: points[0].lat, longitude: points[0].lng, zoom: 10 }
  }
  let minLat = points[0].lat
  let maxLat = points[0].lat
  let minLng = points[0].lng
  let maxLng = points[0].lng
  for (const point of points) {
    if (point.lat < minLat) minLat = point.lat
    if (point.lat > maxLat) maxLat = point.lat
    if (point.lng < minLng) minLng = point.lng
    if (point.lng > maxLng) maxLng = point.lng
  }
  const latitude = (minLat + maxLat) / 2
  const longitude = (minLng + maxLng) / 2
  const latSpan = Math.max(maxLat - minLat, 0.01)
  const lngSpan = Math.max(maxLng - minLng, 0.01)
  const span = Math.max(latSpan, lngSpan)
  // Crude span-to-zoom: smaller spans get tighter zoom. Mapbox's
  // fitBounds would be more precise but adds a useEffect dance —
  // good-enough start values keep the component declarative.
  const zoom = clamp(Math.round(8 - Math.log2(span * 5)), 2, 11)
  return { latitude, longitude, zoom }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
